import { createContext, useContext } from "react";

export type DeepDiveStatus = "pending" | "done" | "failed";

export type DeepDiveTracking =
  | {
      type: "event";
      eventId: string;
      eventName?: string;
      sourceDomain?: string;
      status: DeepDiveStatus;
      analysisId?: string; // Only present for "done" status
      requestId?: string; // Only present for "failed" status
      updatedAt: Date;
    }
  | {
      type: "topic";
      topic: string;
      status: DeepDiveStatus;
      analysisId?: string; // Only present for "done" status
      requestId?: string; // Only present for "failed" status
      updatedAt: Date;
    };

interface DeepDiveTrackingContextType {
  deepDiveTrackings: DeepDiveTracking[];
  updateDeepDiveStatus: (tracking: DeepDiveTracking) => void;
  isEventDeepDiveCompleted: (eventId: string) => boolean;
  isEventDeepDiveFailed: (eventId: string) => boolean;
  getEventDeepDiveTracking: (eventId: string) => DeepDiveTracking | undefined;
  clearDeepDiveTracking: () => void;
  isTopicDeepDiveCompleted: (topic: string) => boolean;
  isTopicDeepDiveFailed: (topic: string) => boolean;
  getTopicDeepDiveTracking: (topic: string) => DeepDiveTracking | undefined;
  getPendingEventDeepDives: () => DeepDiveTracking[];
  getPendingTopicDeepDives: () => DeepDiveTracking[];
  removeDeepDiveTracking: (type: "event" | "topic", id: string) => void;
}

export const DeepDiveTrackingContext = createContext<
  DeepDiveTrackingContextType | undefined
>(undefined);

export const useDeepDiveTracking = () => {
  const context = useContext(DeepDiveTrackingContext);
  if (context === undefined) {
    throw new Error(
      "useDeepDiveTracking must be used within a DeepDiveTrackingProvider",
    );
  }
  return context;
};
